// Editor header chip: whether this record's text is searchable by meaning yet. Opens what was embedded
import { useEffect, useState } from "react"
import { AlertTriangle, ChevronDown, ChevronRight, Loader2, RefreshCw, Sparkles } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { languageName } from "../languages"
import type { IndexedSectionView, RecordIndexStatus } from "../search/useSearchIndex"

const names = (codes: string[]) => codes.map(languageName).join(", ")

function ago(iso?: string): string {
  if (!iso) return ""
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 60) return "just now"
  if (s < 3600) return `${Math.round(s / 60)} min ago`
  if (s < 86400) return `${Math.round(s / 3600)} h ago`
  return new Date(iso).toLocaleDateString()
}

const STATE_LABEL: Record<IndexedSectionView["state"], string> = {
  indexed: "Indexed",
  pending: "Waiting",
  removing: "Being removed",
}

export function IndexBadge({ status, loadDetail, onReindex }: {
  status: RecordIndexStatus | null
  loadDetail: () => Promise<IndexedSectionView[]>
  onReindex: () => void
}) {
  const [open, setOpen] = useState(false)
  const [detail, setDetail] = useState<IndexedSectionView[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)

  // Reloads while open, so it follows indexing as it finishes
  useEffect(() => {
    if (!open) return
    let cancelled = false
    loadDetail().then(
      d => { if (!cancelled) { setDetail(d); setError(null) } },
      e => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)) },
    )
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, status?.state, status?.pendingLocales.join()])

  if (!status) return null
  const { state, indexedLocales, pendingLocales } = status

  const label = state === "indexed" ? "Indexed" : state === "indexing" ? "Indexing" : "Index pending"
  const title = [
    indexedLocales.length && `Searchable by meaning in ${names(indexedLocales)}.`,
    pendingLocales.length && (state === "indexing"
      ? `Indexing ${names(pendingLocales)} now.`
      : `${names(pendingLocales)} will be indexed a few seconds after you stop editing.`),
    "Click to see what was indexed.",
  ].filter(Boolean).join(" ")

  const locales = detail ? [...new Set(detail.map(d => d.locale))] : []

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={title}
          className={cn(
            "inline-flex shrink-0 items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium transition-colors",
            state === "indexed"
              ? "border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 dark:border-violet-900 dark:bg-violet-950/40 dark:text-violet-300"
              : "border-dashed text-muted-foreground hover:bg-muted",
          )}
        >
          {state === "indexing" ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Sparkles className="h-2.5 w-2.5" />}
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96 p-0">
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Sparkles className="h-3.5 w-3.5 text-violet-600" />
          <span className="flex-1 text-xs font-semibold">Meaning search index</span>
          <Button
            size="sm" variant="ghost" className="h-6 gap-1 px-2 text-[11px]"
            disabled={state === "indexing"}
            onClick={onReindex}
            title="Embed any changed text now instead of waiting"
          >
            <RefreshCw className={cn("h-3 w-3", state === "indexing" && "animate-spin")} />
            Index now
          </Button>
        </div>

        <div className="max-h-96 overflow-y-auto p-2">
          {error && (
            <p className="flex items-center gap-1.5 p-2 text-xs text-destructive">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {error}
            </p>
          )}
          {!detail && !error && (
            <p className="flex items-center gap-1.5 p-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
            </p>
          )}
          {detail && !detail.length && (
            <p className="p-2 text-xs text-muted-foreground">This record has no text to index yet.</p>
          )}

          {locales.map(locale => {
            const rows = detail!.filter(d => d.locale === locale)
            const live = rows.filter(r => r.state !== "removing")
            const done = live.filter(r => r.state === "indexed")
            const latest = done.map(r => r.embeddedAt).filter(Boolean).sort().pop()
            return (
              <div key={locale} className="mb-2 last:mb-0">
                <div className="flex items-baseline gap-2 px-1 pb-1">
                  <span className="text-xs font-medium">{languageName(locale)}</span>
                  <span className="text-[10px] text-muted-foreground">
                    {done.length}/{live.length} section{live.length === 1 ? "" : "s"} indexed
                    {latest && ` · ${ago(latest)}`}
                  </span>
                </div>
                <div className="overflow-hidden rounded-md border">
                  {rows.map(r => {
                    const key = `${r.locale}|${r.chunk}|${r.state}`
                    const isOpen = expanded === key
                    return (
                      <div key={key} className="border-b last:border-b-0">
                        <button
                          type="button"
                          onClick={() => setExpanded(isOpen ? null : key)}
                          className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left hover:bg-muted/50"
                        >
                          {isOpen ? <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />}
                          <span className="w-14 shrink-0 text-[10px] tabular-nums text-muted-foreground">Section {r.chunk + 1}</span>
                          <span className="min-w-0 flex-1 truncate text-[11px]">{r.content.split("\n").filter(Boolean).slice(1).join(" ") || r.content}</span>
                          <span className={cn(
                            "shrink-0 rounded px-1 text-[9px] font-medium",
                            r.state === "indexed" && "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300",
                            r.state === "pending" && "bg-muted text-muted-foreground",
                            r.state === "removing" && "bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-200",
                          )}>
                            {STATE_LABEL[r.state]}
                          </span>
                        </button>
                        {isOpen && (
                          <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words bg-muted/30 px-2 py-1.5 text-[10px] leading-snug text-muted-foreground">
                            {r.content}
                          </pre>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>

        <p className="border-t px-3 py-1.5 text-[10px] text-muted-foreground">
          Each section is embedded separately, so a search can match any part of the record.
        </p>
      </PopoverContent>
    </Popover>
  )
}
